export const ACTIVE_STATUS = 1;
export const INACTIVE_STATUS = 0;
export const TRIAL_OVERRIDE_PLAN_ID = 3;
export const PAGE_SIZE = 10;

export function normalizeText(value: any) {
  return String(value || "").trim();
}

function normalizeAddressPart(value: any) {
  return normalizeText(value)
    .toLowerCase()
    .replace(/[.,]/g, " ")
    .replace(/\b(street|st)\b/g, "st")
    .replace(/\b(avenue|ave)\b/g, "ave")
    .replace(/\b(road|rd)\b/g, "rd")
    .replace(/\b(drive|dr)\b/g, "dr")
    .replace(/\b(lane|ln)\b/g, "ln")
    .replace(/\b(court|ct)\b/g, "ct")
    .replace(/\b(boulevard|blvd)\b/g, "blvd")
    .replace(/\s+/g, " ")
    .trim();
}

export function formatDateTime(value: any) {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  if (raw.toLowerCase() === "neverexpires") return "Never expires";

  const asNumber = Number(raw);
  let parsed: Date | null = null;
  if (Number.isFinite(asNumber) && raw.length >= 10) {
    parsed = new Date(raw.length >= 13 ? asNumber : asNumber * 1000);
  } else {
    const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
    const candidate = new Date(normalized);
    if (!Number.isNaN(candidate.getTime())) parsed = candidate;
  }
  if (!parsed || Number.isNaN(parsed.getTime())) return raw;

  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function getPrimaryPhone(business: any) {
  const first = business?.phoneNumbers?.find((row: any) => normalizeText(row?.phoneNumber));
  if (!first) return "";
  const code = normalizeText(first?.countryCode);
  const number = normalizeText(first?.phoneNumber);
  return `${code}${code && number ? " " : ""}${number}`.trim();
}

export function getPhoneHref(phone: string) {
  return `tel:${String(phone || "").replace(/[^\d+]/g, "")}`;
}

export function getPrimaryEmail(business: any) {
  return normalizeText(business?.emails?.find((item: any) => normalizeText(item)));
}

export function getLocationLine(business: any) {
  return [business?.cityName, business?.stateCode, business?.zipcode]
    .map((part) => normalizeText(part))
    .filter(Boolean)
    .join(", ");
}

export function getFullAddress(business: any) {
  const street = normalizeText(business?.street);
  const address = normalizeText(business?.address);
  const normalizedStreet = normalizeAddressPart(street);
  const normalizedAddress = normalizeAddressPart(address);
  const shouldIncludeAddress =
    normalizedAddress &&
    normalizedAddress !== normalizedStreet &&
    !normalizedStreet.includes(normalizedAddress) &&
    !normalizedAddress.includes(normalizedStreet);

  return [
    street,
    shouldIncludeAddress ? address : null,
    business?.cityName,
    business?.stateCode,
    business?.zipcode,
  ]
    .map((part) => normalizeText(part))
    .filter(Boolean)
    .join(", ");
}

export function getMapHref(business: any) {
  const query = getFullAddress(business);

  if (!query) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export function isBusinessActive(business: any) {
  return Number(business?.status) === ACTIVE_STATUS;
}

export function isBusinessClaimed(business: any) {
  const status = normalizeText(business?.claimStatus).toUpperCase();
  if (["CLAIMED", "VERIFIED", "APPROVED", "OWNED"].includes(status)) return true;
  return business?.createdBy !== null && business?.createdBy !== undefined;
}

export function isCareDirectoryApproved(business: any) {
  const careSettings = business?.careSettings || {};
  const verified = !!careSettings?.afhLicenseVerified;
  const status = normalizeText(
    careSettings?.licenseStatus || careSettings?.facilityStatusCode
  ).toUpperCase();
  const statusOk = !status || status === "ACTIVE" || status === "OP";
  const expiresRaw = normalizeText(careSettings?.licenseExpiresOn);
  if (!verified || !statusOk) return false;
  if (!expiresRaw) return true;
  const expires = new Date(expiresRaw);
  return Number.isNaN(expires.getTime()) || expires >= new Date();
}

export function isCareDirectoryVisible(business: any) {
  return Boolean(business?.displayOnBookingPage) && isCareDirectoryApproved(business);
}

export function hasTrialOverride(business: any) {
  const expiresAt = normalizeText(business?.exemptedPlanExpiresAt);
  if (!expiresAt) return false;
  if (expiresAt.toLowerCase() === "neverexpires") return true;
  const parsed = new Date(expiresAt);
  return !Number.isNaN(parsed.getTime()) && parsed > new Date();
}

export function buildBusinessUpdateInput(business: any, patch: Record<string, any>) {
  return {
    id: Number(business?.id),
    businessCategoryId:
      business?.businessCategoryId == null ? null : Number(business.businessCategoryId),
    name: business?.name || "",
    description: business?.description || "",
    currency: business?.currency || "USD",
    status: Number(business?.status ?? ACTIVE_STATUS),
    street: business?.street || "",
    countryId: business?.countryId == null ? null : Number(business.countryId),
    stateId: business?.stateId == null ? null : Number(business.stateId),
    cityId: business?.cityId == null ? null : Number(business.cityId),
    zipcode: business?.zipcode || "",
    cityName: business?.cityName || "",
    stateName: business?.stateName || "",
    stateCode: business?.stateCode || "",
    address: business?.address || "",
    createdBy: business?.createdBy == null ? null : Number(business.createdBy),
    phoneNumbers: Array.isArray(business?.phoneNumbers)
      ? business.phoneNumbers.map((item: any) => ({
          countryCode: item?.countryCode || "",
          phoneNumber: item?.phoneNumber || "",
        }))
      : [],
    faxNumber: business?.faxNumber || "",
    emails: Array.isArray(business?.emails) ? business.emails : [],
    url: business?.url || "",
    links: Array.isArray(business?.links)
      ? business.links.map((item: any) => ({
          platform: item?.platform || "",
          url: item?.url || "",
        }))
      : [],
    stage: business?.stage == null ? null : Number(business.stage),
    displayOnBookingPage: Boolean(business?.displayOnBookingPage),
    claimStatus: business?.claimStatus || "",
    exemptedPlanId:
      business?.exemptedPlanId == null ? null : Number(business.exemptedPlanId),
    exemptedPlanExpiresAt: business?.exemptedPlanExpiresAt || null,
    ...patch,
  };
}

export function buildExtendedTrialDate(currentValue: any, days: number) {
  const raw = normalizeText(currentValue);
  const now = new Date();
  const base = raw && raw.toLowerCase() !== "neverexpires" ? new Date(raw) : now;
  const start = !Number.isNaN(base.getTime()) && base > now ? base : now;
  const next = new Date(start.getTime());
  next.setDate(next.getDate() + days);
  return next.toISOString();
}
